import { ColorSpace, contrastAPCA, sRGB } from 'colorjs.io/fn';

import { THEME_COLORS } from '@lib/utils/bootstrapColors';

// The functional API: only sRGB and APCA reach the bundle
ColorSpace.register(sRGB);

type RGB = { r: number; g: number; b: number };
type RGBA = RGB & { a: number };

/** Expand #abc -> #aabbcc and parse hex to RGB */
function hexToRgb(hex: string): RGB | null {
	let h = hex.trim();
	const shorthand = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	h = h.replace(shorthand, (_, r, g, b) => r + r + g + g + b + b);

	const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
	return m ? { r: Number.parseInt(m[1], 16), g: Number.parseInt(m[2], 16), b: Number.parseInt(m[3], 16) } : null;
}

/** Try to parse any CSS color using canvas normalization (works for named/rgb/hsl/etc.) */
function cssColorToRgba(color: string): RGBA | null {
	// Hex needs no canvas, which a test environment lacks
	const hex = hexToRgb(color);
	if (hex) return { ...hex, a: 1 };
	if (typeof window === 'undefined') return null;

	const canvas =
		(cssColorToRgba as any)._canvas || ((cssColorToRgba as any)._canvas = document.createElement('canvas'));
	const ctx = canvas.getContext('2d');
	if (!ctx) return null;

	ctx.fillStyle = '#000';
	try {
		ctx.fillStyle = color as any;
	} catch {
		return null;
	}

	const normalized = ctx.fillStyle.trim();
	const rgbaMatch = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(normalized);

	if (rgbaMatch) {
		const r = Math.round(Number.parseFloat(rgbaMatch[1]));
		const g = Math.round(Number.parseFloat(rgbaMatch[2]));
		const b = Math.round(Number.parseFloat(rgbaMatch[3]));
		const a = rgbaMatch[4] !== undefined ? Math.max(0, Math.min(1, Number.parseFloat(rgbaMatch[4]))) : 1;
		return { r, g, b, a };
	}

	const rgb = hexToRgb(normalized);
	return rgb ? { ...rgb, a: 1 } : null;
}

/** Composite RGBA over white to handle transparency */
function compositeOverWhite({ r, g, b, a }: RGBA): RGB {
	if (a >= 1) return { r, g, b };
	return {
		r: Math.round(r * a + 255 * (1 - a)),
		g: Math.round(g * a + 255 * (1 - a)),
		b: Math.round(b * a + 255 * (1 - a)),
	};
}

/** The dark ink of the theme, Bootstrap's body colour, the alternative to white. */
export const DARK_INK = THEME_COLORS.dark;

/** A colour as colorjs.io takes it. */
function srgb({ r, g, b }: RGB) {
	return { space: sRGB, coords: [r / 255, g / 255, b / 255] as [number, number, number], alpha: 1 };
}

const WHITE = srgb({ r: 255, g: 255, b: 255 });
const DARK = srgb(hexToRgb(DARK_INK)!);

/**
 * The text colour that reads better on a fill of the given CSS colour: white or the dark ink,
 * whichever has the larger APCA contrast against it. APCA rather than the WCAG 2 ratio: that
 * one misjudges saturated mid tones and put the dark ink on orange, where white reads better.
 * One rule for every place that sets text on a colour - the buttons on the primary, the colour
 * field, the pill. An empty or unparsable colour keeps white.
 */
export function getContrastColor(color: string): '#fff' | typeof DARK_INK {
	const parsed = color.trim() ? cssColorToRgba(color) : null;
	if (!parsed) return '#fff';

	const background = srgb(compositeOverWhite(parsed));

	return Math.abs(contrastAPCA(background, WHITE)) >= Math.abs(contrastAPCA(background, DARK)) ? '#fff' : DARK_INK;
}

/** Vue composable wrapper - returns a reactive helper */
export function useContrastColor() {
	return { getContrastColor };
}
